/**
 * Stockage de la session de poste/rôle (CDC §7.3) — jamais une personne.
 * La session porte `{ post, network, permissions }` et un jeton opaque.
 *
 * Persistée dans `localStorage` pour survivre à un rechargement du poste ;
 * jamais transmise ailleurs qu'à l'API Sentinelle.
 */

import type { Session } from "../contract/types";
import { legacyKeyOf, readWithLegacyFallback } from "../app/legacyStorage";

const STORAGE_KEY = "sentinelle.session.v1";
const LEGACY_KEY = legacyKeyOf(STORAGE_KEY);

export interface StoredSession {
  token: string;
  session: Session;
}

type Listener = (value: StoredSession | null) => void;

function read(): StoredSession | null {
  try {
    const raw = readWithLegacyFallback(STORAGE_KEY, LEGACY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (typeof parsed?.token === "string" && parsed?.session) return parsed;
    return null;
  } catch {
    return null;
  }
}

function write(value: StoredSession | null): void {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* mode privé / stockage bloqué : la session reste en mémoire seulement */
  }
}

export class SessionStore {
  private current: StoredSession | null = read();
  private readonly listeners = new Set<Listener>();

  get(): StoredSession | null {
    return this.current;
  }

  getToken(): string | null {
    return this.current?.token ?? null;
  }

  set(value: StoredSession | null): void {
    this.current = value;
    write(value);
    for (const listener of [...this.listeners]) listener(value);
  }

  clear(): void {
    this.set(null);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

/** Instance unique pour toute l'application. */
export const sessionStore = new SessionStore();
